import { useEffect } from 'react'
import { flushSync } from 'react-dom'
import type { Motion } from '../../shared/preferences'

// How things move in the Modern style of Photos (styles/modern.css has the curves and timings). Each change of view
// is a view transition where the browser has them: the browser photographs the page before and after, and the photo
// you tapped flies to where it lands instead of the page cutting. Where it doesn't, or when someone has asked their
// device (or their preferences) for less motion, the change just happens.

// Smooth or Simple motion, from the account (Preferences), on the page root for the stylesheets (styles/motion.css).
// public/theme.js puts this device's last choice there before the first paint.
const MOTION_KEY = 'gatheredfrom:motion'
export function applyMotion(motion: Motion) {
  document.documentElement.dataset.motion = motion
  try {
    localStorage.setItem(MOTION_KEY, motion)
  } catch {
    // Storage can be off; the account still has the choice.
  }
}
export function useMotion(motion: Motion | undefined) {
  useEffect(() => {
    if (motion) applyMotion(motion)
  }, [motion])
}

// Pictures loaded as you scroll fade in once they've arrived (styles/motion.css), so each is marked as it loads, or
// fails, which shows its placeholder. Loads don't bubble, so the page listens for them on the way down. Listening
// for touches at all is also what lets iOS show a tile pressed in (:active) under a finger.
export function watchPictures() {
  const mark = (e: Event) => {
    if (e.target instanceof HTMLImageElement) e.target.dataset.loaded = ''
  }
  document.addEventListener('load', mark, true)
  document.addEventListener('error', mark, true)
  document.addEventListener('touchstart', () => {}, { passive: true })
}

// Less motion: the device asks for it, or the person picked Simple.
export const reducedMotion = () =>
  (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) ||
  (typeof document !== 'undefined' && document.documentElement.dataset.motion === 'simple')

type ViewTransition = { finished: Promise<void>; skipTransition: () => void }
type WithTransitions = Document & { startViewTransition?: (update: () => void) => ViewTransition }

let running: ViewTransition | null = null
// How long the browser has to start a transition before the change is made without one, in milliseconds.
const STALL = 500

// Runs change() (a React state update) as a view transition. kind names it on the page root (data-morph) for the
// length of the transition, so the stylesheet can say which parts take part: the wall's photos when it's narrowed,
// one photo when it opens. prepare runs just before the "before" picture is taken, landed once the change is on the
// page (before the "after" picture), and done when it's all over.
export function morph(
  kind: 'wall' | 'open' | 'close' | 'swap',
  change: () => void,
  { prepare, landed, done }: { prepare?: () => void; landed?: () => void; done?: () => void } = {},
) {
  const doc = document as WithTransitions
  if (!doc.startViewTransition || reducedMotion() || doc.visibilityState !== 'visible') {
    change()
    done?.()
    return
  }
  try {
    running?.skipTransition()
  } catch {
    // Already over.
  }
  const root = document.documentElement
  root.dataset.morph = kind
  prepare?.()
  // The change happens exactly once, and the morph ends exactly once, whatever the browser does. If it hasn't asked
  // for the change soon (WebKit can stall before taking the "before" picture, and may then never finish or even refuse
  // to be skipped), the change is made anyway and the morph ends there: a cut beats a page that didn't respond.
  let made = false
  let ended = false
  let stalled = 0
  let transition: ViewTransition | null = null
  const update = () => {
    if (made) return
    made = true
    clearTimeout(stalled)
    flushSync(change)
    landed?.()
  }
  const end = () => {
    if (ended) return
    ended = true
    clearTimeout(stalled)
    if (running === transition) {
      running = null
      delete root.dataset.morph
    }
    done?.()
  }
  try {
    transition = doc.startViewTransition(update)
  } catch {
    running = null
    update()
    end()
    return
  }
  running = transition
  stalled = window.setTimeout(() => {
    try {
      update()
    } finally {
      try {
        transition.skipTransition()
      } catch {
        // Nothing left to skip.
      }
      end()
    }
  }, STALL)
  void transition.finished.catch(() => {}).finally(end)
}

// The light a photo gives off: its colours averaged, the vivid ones counted more, so the room around it can glow
// with it. Read from the small copy already on screen; null when it can't be read. Kept per photo for the visit.
const glows = new Map<string, string>()
export function glowOf(id: string, img: HTMLImageElement | null): string | null {
  const known = glows.get(id)
  if (known || !img || !img.complete || !img.naturalWidth) return known ?? null
  try {
    const size = 8
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.drawImage(img, 0, 0, size, size)
    const { data } = ctx.getImageData(0, 0, size, size)
    let r = 0
    let g = 0
    let b = 0
    let weight = 0
    for (let i = 0; i < data.length; i += 4) {
      const [pr, pg, pb] = [data[i]!, data[i + 1]!, data[i + 2]!]
      const vivid = Math.max(pr, pg, pb) - Math.min(pr, pg, pb)
      const w = 1 + vivid / 32
      r += pr * w
      g += pg * w
      b += pb * w
      weight += w
    }
    const glow = `${Math.round(r / weight)} ${Math.round(g / weight)} ${Math.round(b / weight)}`
    glows.set(id, glow)
    return glow
  } catch {
    // A photo the canvas may not read (it never should be, they're all our own) just gives no glow.
    return null
  }
}

// A drag's speed at the end, in pixels per millisecond, from its last few moves, to tell a flick from a slow pull.
export function velocity(points: { t: number; x: number; y: number }[]) {
  const last = points.at(-1)
  const first = points.find((p) => last && last.t - p.t < 90) ?? points[0]
  if (!last || !first || last.t === first.t) return { x: 0, y: 0 }
  return { x: (last.x - first.x) / (last.t - first.t), y: (last.y - first.y) / (last.t - first.t) }
}

// Pulled past the end: it gives, less and less, as a phone's scroll does at its edge.
export const rubber = (distance: number, limit = 120) =>
  (Math.sign(distance) * limit * Math.abs(distance)) / (limit + Math.abs(distance))
